import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { v4 as uuidV4 } from 'uuid';

import Icon from '../Icon';

import style from './DialogBox.module.scss';

/*
 * Handle setting body/scroll area `overflow: hidden` globally when modal is open.
 * This way if there are two modals open, and one closes, the body is still not scrollable:
 */
const xmDialogsOpen = new Set();
const updateScrollability = () => {
  const body = document.querySelector('body');
  if (xmDialogsOpen.size > 0) {
    body.classList.add('spark-dialog-overflow-hidden');
  } else {
    body.classList.remove('spark-dialog-overflow-hidden');
  }
};
const trackDialog = uuid => {
  xmDialogsOpen.add(uuid);
  updateScrollability();
};
const untrackDialog = uuid => {
  xmDialogsOpen.delete(uuid);
  updateScrollability();
};

/**
 * DialogBox displays a dialog box. You can pass in left and right button controls, as well as arbitrary content inside it. All state management should be handled by the consumer (parent component).
 */
export default function DialogBox({
  isOpen,
  onClose,
  titleText,
  headerContent,
  children,
  leftButtons,
  rightButtons,
  centerButtons,
  simpleStyle,
  shouldCloseOnEscape,
  shouldCloseOnOverlayClick,
  classes,
  helpLinkComponent,
  helpLink,
  onHelp,
  allowMaximize,
  centeredTitle,
  'data-testid': testid,
  testids,
  initiallyMaximized,
}) {
  const uuidRef = useRef(uuidV4());
  const [isMaximized, setIsMaximized] = useState(initiallyMaximized);

  useEffect(() => {
    const handleKeyDown = ({ keyCode }) => {
      // 27 is keyCode for ESC
      if (keyCode !== 27) {
        return;
      }

      if (isOpen && shouldCloseOnEscape) {
        onClose();
      }
    };

    const uuid = uuidRef.current;
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      trackDialog(uuid);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      untrackDialog(uuid);
    };
  }, [isOpen, shouldCloseOnEscape, uuidRef.current]);

  const headerButtonsRef = useRef(null);
  const [headerButtonWidth, setHeaderButtonWidth] = useState(0);
  const hasHelp = helpLinkComponent || helpLink || onHelp;
  useLayoutEffect(() => {
    if (headerButtonsRef.current) {
      setHeaderButtonWidth(headerButtonsRef.current.clientWidth);
    }
  }, [hasHelp, allowMaximize, isOpen]);

  if (!isOpen) return null;

  const headerIcons = (
    <div
      className={classnames(style.headerIcons, {
        [style.insideCustomHeader]: !!headerContent,
      })}
      ref={headerButtonsRef}
    >
      {helpLinkComponent && (
        <span className={style.helpIconComponentWrapper}>
          {helpLinkComponent}
        </span>
      )}
      {hasHelp && !helpLinkComponent && (
        <a
          className={style.headerIcon}
          data-testid="helpButton"
          href={helpLink}
          onClick={onHelp}
          rel="noopener noreferrer"
          target="_blank"
        >
          <Icon type="icon-question" />
        </a>
      )}
      {allowMaximize && (
        <Icon
          type={isMaximized ? 'icon-minimize' : 'icon-maximize'}
          className={classnames(style.headerIcon, style.resizeIcon)}
          onClick={() => {
            setIsMaximized(!isMaximized);
          }}
          data-testid="resizeButton"
        />
      )}
      <Icon
        type="icon-cancel"
        className={classnames(style.headerIcon, style.closeIcon)}
        onClick={onClose}
        data-testid={testids?.cancelButton || 'icon-dialogBox-cancel'}
      />
    </div>
  );

  return (
    <div
      className={classnames(
        style.backdrop,
        classes.backdrop,
        // TODO: change this to a CSS module class at some point
        'xm-global-dialog-box-backdrop',
        {
          [style.maximized]: isMaximized,
          [classes.maximized]: isMaximized,
        },
      )}
      onClick={event => {
        if (event.target !== event.currentTarget) {
          return;
        }

        if (isOpen && shouldCloseOnOverlayClick) {
          onClose();
        }
      }}
    >
      {isOpen && <style>{'[data-theme] { z-index: 1000; }'}</style>}
      <div
        className={classnames(style.wrap, classes.wrap)}
        data-testid={testid}
      >
        {headerContent && (
          <div
            className={classnames(
              style.header,
              style.shaded,
              style.customHeader,
              classes.header,
            )}
          >
            {headerIcons}
            {headerContent}
          </div>
        )}
        {!headerContent && (
          <div
            className={classnames(
              style.header,
              style.standardHeader,
              {
                [style.shaded]: !simpleStyle,
              },
              classes.header,
            )}
          >
            {(centeredTitle || simpleStyle) && (
              <div
                style={{
                  width: headerButtonWidth,
                  flexShrink: 0,
                }}
              />
            )}
            <div className={classnames(style.title, classes.title)}>
              {titleText}
            </div>
            {headerIcons}
          </div>
        )}
        <div className={classnames(style.content, classes.content)}>
          {children}
        </div>
        {(leftButtons || rightButtons || centerButtons) && (
          <div
            className={classnames(
              style.buttons,
              {
                [style.shaded]: !simpleStyle,
              },
              classes.buttons,
            )}
          >
            {leftButtons && <div className={style.left}>{leftButtons}</div>}
            {centerButtons && (
              <div className={style.center}>{centerButtons}</div>
            )}
            {rightButtons && <div className={style.right}>{rightButtons}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

DialogBox.displayName = 'DialogBox';

DialogBox.propTypes = {
  /**
   * decides whether the dialog is shown or hidden
   */
  isOpen: PropTypes.bool.isRequired,
  /**
   * function invoked when the dialog is closed
   */
  onClose: PropTypes.func.isRequired,
  /**
   * component to be rendered instead of the standard dialog header
   */
  headerContent: PropTypes.node,
  /**
   * title text of the dialog
   */
  titleText: PropTypes.node,
  /**
   * contents of the dialog
   */
  children: PropTypes.node.isRequired,
  /**
   * buttons shown on the left side of the dialog footer
   */
  leftButtons: PropTypes.node,
  /**
   * buttons shown on the right side of the dialog footer
   */
  rightButtons: PropTypes.node,
  /**
   * buttons shown in the center of the dialog footer
   */
  centerButtons: PropTypes.node,
  /**
   * enables the user to close the dialog by clicking on the dialog overlay
   */
  shouldCloseOnOverlayClick: PropTypes.bool,
  /**
   * enables the user to close the dialog by pressing `esc`
   */
  shouldCloseOnEscape: PropTypes.bool,
  /**
   * enables simplified stlying for things like confirm dialogs
   */
  simpleStyle: PropTypes.bool,
  /**
   * classNames to add to sections of the DialogBox
   */
  classes: PropTypes.shape({
    backdrop: PropTypes.string,
    wrap: PropTypes.string,
    header: PropTypes.string,
    title: PropTypes.string,
    content: PropTypes.string,
    buttons: PropTypes.string,
    maximized: PropTypes.string,
  }),
  /**
   * when provided, this component will be rendered in the helpLink area of the header buttons
   */
  helpLinkComponent: PropTypes.node,
  /**
   * when provided, a help button will show up in the dialog header
   * allowing the user to navigate to the given link
   */
  helpLink: PropTypes.string,
  /**
   * when provided, a help button will show up in the dialog header. Upon
   * clicking the button, this function will be invoked. Overrides *helpLink*
   */
  onHelp: PropTypes.func,
  /**
   * When enabled a maximize/minimize button will appear in the dialog header.
   * This button will toggle a maximized class on the dialog backdrop (top level element for flexibility).
   */
  allowMaximize: PropTypes.bool,
  /**
   * centers the dialog title for non `simpleStyle` dialogs
   * cannot be used to left align titles for `simpleStyle` dialogs
   */
  centeredTitle: PropTypes.bool,
  /**
   * test id passed in to the `wrap` div
   */
  'data-testid': PropTypes.string,
  testids: PropTypes.shape({
    cancelButton: PropTypes.string,
  }),
  /**
   * whether the dialog is initially maximized
   */
  initiallyMaximized: PropTypes.bool,
};

DialogBox.defaultProps = {
  leftButtons: null,
  rightButtons: null,
  centerButtons: null,
  shouldCloseOnOverlayClick: false,
  shouldCloseOnEscape: true,
  simpleStyle: false,
  classes: {
    backdrop: '',
    wrap: '',
    header: '',
    title: '',
    content: '',
    buttons: '',
    maximized: '',
  },
  helpLinkComponent: null,
  helpLink: null,
  onHelp: null,
  allowMaximize: false,
  centeredTitle: false,
  'data-testid': undefined,
  headerContent: undefined,
  testids: {},
  titleText: undefined,
  initiallyMaximized: false,
};
